<script lang="ts">
  /**
   * Interaktiv preview-karta för snap-iterationen (S-04-28 Del 2).
   *
   * Renderar:
   *  - Snappad polyline (eller fallback raka linjer) som dimmad linje
   *  - Pre-snap-waypoints som klickbara cirklar med nummer
   *
   * Två edit-händelser emitteras:
   *  - `onWaypointRemove(idx)` — admin klickade på waypoint nummer idx
   *  - `onWaypointAdd(coord)` — admin klickade på kartan vid en koord
   *    (vi lägger ALLTID till i slutet — sortering av insert-position
   *    hanteras separat post-add via re-snap).
   *
   * Hard-rules (CLAUDE.md):
   *  - Inga state i localStorage. Reload = clean.
   *  - Komponenten är pure visualization — ingen direkt-mutation av
   *    parent-state. Alla edits går via callbacks.
   */
  import { onDestroy } from 'svelte';
  import maplibregl, { type Map as MapLibreMap, type StyleSpecification } from 'maplibre-gl';
  import 'maplibre-gl/dist/maplibre-gl.css';
  import { readProtomapsApiKey } from '../../config/map';
  import { vectorDarkStyle } from '../../config/mapStyles';
  import { renderColorFor } from '../util/colorPalette';
  import type { Coordinate, Polyline } from '../draw/types';
  import type { HearthPriority } from './hearthsClient';

  interface Props {
    waypoints: ReadonlyArray<Coordinate>;
    snappedLines: ReadonlyArray<Polyline>;
    priority: HearthPriority;
    displayColor?: string | null;
    /* eslint-disable no-unused-vars */
    onWaypointRemove?: (index: number) => void;
    onWaypointAdd?: (coord: Coordinate) => void;
    /* eslint-enable no-unused-vars */
  }

  const {
    waypoints,
    snappedLines,
    priority,
    displayColor = null,
    onWaypointRemove,
    onWaypointAdd,
  }: Props = $props();

  const apiKey: string | null = $derived(readProtomapsApiKey());

  let mapContainer: HTMLDivElement | undefined = $state();
  let mapInstance: MapLibreMap | null = null;

  const SNAP_SOURCE = 'snap-preview-line';
  const SNAP_LAYER = 'snap-preview-line-layer';
  const WP_SOURCE = 'snap-preview-waypoints';
  const WP_LAYER_CIRCLE = 'snap-preview-waypoints-circle';
  const WP_LAYER_LABEL = 'snap-preview-waypoints-label';

  function snappedAsGeoJson(): GeoJSON.FeatureCollection<GeoJSON.LineString> {
    return {
      type: 'FeatureCollection',
      features: snappedLines
        .filter((l) => l.points.length >= 2)
        .map((l) => ({
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: l.points.map((p) => [p.longitude, p.latitude]),
          },
        })),
    };
  }

  function waypointsAsGeoJson(): GeoJSON.FeatureCollection<GeoJSON.Point> {
    return {
      type: 'FeatureCollection',
      features: waypoints.map((wp, idx) => ({
        type: 'Feature' as const,
        properties: { idx, label: String(idx + 1) },
        geometry: {
          type: 'Point' as const,
          coordinates: [wp.longitude, wp.latitude],
        },
      })),
    };
  }

  function bbox(): [[number, number], [number, number]] | null {
    const all: Array<[number, number]> = [
      ...waypoints.map((wp) => [wp.longitude, wp.latitude] as [number, number]),
      ...snappedLines.flatMap((l) =>
        l.points.map((p) => [p.longitude, p.latitude] as [number, number]),
      ),
    ];
    if (all.length === 0) return null;
    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;
    for (const [lng, lat] of all) {
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    }
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ];
  }

  $effect(() => {
    if (!mapContainer || !apiKey) return;
    const style: StyleSpecification | null = vectorDarkStyle(apiKey);
    if (!style) return;

    const bb = bbox();
    const center: [number, number] = bb
      ? [(bb[0][0] + bb[1][0]) / 2, (bb[0][1] + bb[1][1]) / 2]
      : [11.95, 57.74];
    const map = new maplibregl.Map({
      container: mapContainer,
      style,
      center,
      zoom: 14,
      attributionControl: { compact: true },
    });
    mapInstance = map;

    map.once('load', () => {
      // Snappad linje (dimmad så waypoints syns ovanpå).
      map.addSource(SNAP_SOURCE, { type: 'geojson', data: snappedAsGeoJson() });
      map.addLayer({
        id: SNAP_LAYER,
        type: 'line',
        source: SNAP_SOURCE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': renderColorFor(displayColor, priority),
          'line-width': 4,
          'line-opacity': 0.6,
        },
      });

      // Waypoint-cirklar (klickbara).
      map.addSource(WP_SOURCE, { type: 'geojson', data: waypointsAsGeoJson() });
      map.addLayer({
        id: WP_LAYER_CIRCLE,
        type: 'circle',
        source: WP_SOURCE,
        paint: {
          'circle-radius': 12,
          'circle-color': '#f4d589',
          'circle-stroke-color': '#0e0e10',
          'circle-stroke-width': 2,
          'circle-opacity': 0.95,
        },
      });
      // Waypoint-nummer ovanpå cirkeln.
      map.addLayer({
        id: WP_LAYER_LABEL,
        type: 'symbol',
        source: WP_SOURCE,
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 12,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#0e0e10',
        },
      });

      // fitBounds.
      const bb2 = bbox();
      if (bb2) {
        map.fitBounds(bb2, { padding: 48, duration: 0, maxZoom: 17 });
      }

      // Cursor-feedback vid waypoint-hover.
      map.on('mouseenter', WP_LAYER_CIRCLE, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', WP_LAYER_CIRCLE, () => {
        map.getCanvas().style.cursor = '';
      });

      // Klick på waypoint-cirkel → onWaypointRemove(idx).
      map.on('click', WP_LAYER_CIRCLE, (e) => {
        e.preventDefault();
        const feat = e.features?.[0];
        if (!feat) return;
        const idx = (feat.properties as { idx?: number }).idx;
        if (typeof idx === 'number') {
          onWaypointRemove?.(idx);
        }
      });

      // Klick utanför waypoint → onWaypointAdd(coord). Vi använder
      // map-level click och kontrollerar via queryRenderedFeatures
      // att vi inte träffat en waypoint-cirkel.
      map.on('click', (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: [WP_LAYER_CIRCLE],
        });
        if (features.length > 0) return; // hanteras av layer-click
        onWaypointAdd?.({
          latitude: e.lngLat.lat,
          longitude: e.lngLat.lng,
        });
      });
    });

    return () => {
      map.remove();
      mapInstance = null;
    };
  });

  // Re-render source när waypoints eller snappedLines ändras (efter
  // remove/add). Vi behöver inte re-skapa kartan — bara byta data.
  $effect(() => {
    if (!mapInstance) return;
    if (mapInstance.getSource(SNAP_SOURCE)) {
      const src = mapInstance.getSource(SNAP_SOURCE) as maplibregl.GeoJSONSource;
      src.setData(snappedAsGeoJson());
    }
    if (mapInstance.getSource(WP_SOURCE)) {
      const src = mapInstance.getSource(WP_SOURCE) as maplibregl.GeoJSONSource;
      src.setData(waypointsAsGeoJson());
    }
  });

  onDestroy(() => {
    mapInstance?.remove();
    mapInstance = null;
  });
</script>

{#if !apiKey}
  <div class="preview-fallback" role="status">
    Kart-förhandsvisning otillgänglig — Protomaps-nyckel saknas.
  </div>
{:else}
  <div class="preview-container" bind:this={mapContainer} aria-label="Förhandsgranskning av snap-resultat"></div>
{/if}

<style>
  .preview-container {
    width: 100%;
    height: 360px;
    border-radius: 4px;
    border: 1px solid var(--rule);
    overflow: hidden;
  }
  .preview-fallback {
    padding: 1rem;
    border: 1px dashed var(--rule);
    border-radius: 4px;
    color: var(--text-dim);
    text-align: center;
    font-size: 0.9rem;
  }
</style>
